import { Left, left } from 'fp-ts/Either';
import { parseValidationErrors } from './parseValidationErrors';

export interface ErrorLeft {
  name: string;
  message: string;
  raw?: unknown;
}

interface ErrorToLeftOptions {
  fallbackMessage?: string;
  parseValidation?: boolean;
  preserveStringMessage?: boolean;
  preserveErrorMessage?: boolean;
  includeRaw?: boolean;
}

function isErrorLike(
  value: unknown,
): value is { name?: unknown; message?: unknown } {
  return typeof value === 'object' && value !== null;
}

export function errorToLeft(
  error: unknown,
  {
    fallbackMessage = 'Something went wrong',
    parseValidation = false,
    preserveStringMessage = true,
    preserveErrorMessage = true,
    includeRaw = false,
  }: ErrorToLeftOptions = {},
): Left<ErrorLeft> {
  if (parseValidation) {
    const parsedMessage = parseValidationErrors(error);

    if (
      parsedMessage !==
      'An error occurred. Please check your form and try again'
    ) {
      return left({
        name: 'ValidationError',
        message: parsedMessage,
        ...(includeRaw ? { raw: error } : {}),
      }) as Left<ErrorLeft>;
    }
  }

  if (typeof error === 'string') {
    return left({
      name: 'Error',
      message: preserveStringMessage ? error : fallbackMessage,
      ...(includeRaw ? { raw: error } : {}),
    }) as Left<ErrorLeft>;
  }

  if (error instanceof Error) {
    return left({
      name: error.name,
      message: preserveErrorMessage ? error.message : fallbackMessage,
      ...(includeRaw ? { raw: error } : {}),
    }) as Left<ErrorLeft>;
  }

  if (isErrorLike(error)) {
    const name =
      typeof error.name === 'string' && error.name.length > 0
        ? error.name
        : 'Error';
    const message =
      typeof error.message === 'string' && error.message.length > 0
        ? error.message
        : fallbackMessage;

    return left({
      name,
      message,
      ...(includeRaw ? { raw: error } : {}),
    }) as Left<ErrorLeft>;
  }

  return left({
    name: 'Error',
    message: fallbackMessage,
    ...(includeRaw ? { raw: error } : {}),
  }) as Left<ErrorLeft>;
}
